// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormField, Input, SpaceBetween } from '@amzn/awsui-components-react-v3/polaris';
import ApiClient from '../../../ApiClient';
import { ArcRowsUpload } from '../../Common/ArcRows';
import { RetryAlert } from '../../Common/ErrorMessages';
import { formatLocale, useLocales } from '../../../utils/locales';
import { byArc } from '../../../utils/arcRows';
import { ApiError } from '../../../utils/serverErrors';
import { DefaultTerField, MetadataInput } from './MetadataFields';
import * as terGroups from './terGroups';

const [WORKFLOW, ...OPTIONAL_FIELDS] = terGroups.METADATA_FIELDS;

export const TEMPLATE_URL = `${process.env.PUBLIC_URL || ''}/ter-group-template.xlsx`;

interface TerGroupCreateProps {
  apiClient: {
    httpGet: (path: string) => Promise<unknown>;
    httpPost: (path: string, body: object) => Promise<unknown>;
  };
  navigate: (path: string) => void;
  canWrite: boolean;
}

export const TerGroupCreate = ({
  apiClient,
  navigate,
  canWrite,
}: TerGroupCreateProps): JSX.Element | null => {
  const [groupName, setGroupName] = useState('');
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [workflowServerError, setWorkflowServerError] = useState<string | undefined>(undefined);
  // The required-workflow error waits until the field is used, like the default TER's.
  const [workflowTouched, setWorkflowTouched] = useState(false);
  const [metadata, setMetadata] = useState<terGroups.Metadata>(terGroups.emptyMetadata);
  const [submitting, setSubmitting] = useState(false);
  const { locales, errors: localeErrors, reload: loadLocales } = useLocales(
    apiClient,
    terGroups.describeError
  );
  // Loaded once for the duplicate-name check; if the load fails, the check is skipped.
  const [existingGroups, setExistingGroups] = useState<terGroups.TerGroupSummary[]>([]);

  useEffect(() => {
    if (!canWrite) {
      return undefined;
    }
    let active = true;
    apiClient
      .httpGet(terGroups.TER_GROUPS_PATH)
      .then(response => {
        if (active && Array.isArray(response)) {
          setExistingGroups(response as terGroups.TerGroupSummary[]);
        }
      })
      .catch(() => undefined);
    return (): void => {
      active = false;
    };
  }, [apiClient, canWrite]);

  if (!canWrite) {
    return null;
  }

  const duplicateName = terGroups.nameTaken(groupName, existingGroups);
  const groupNameError =
    nameError || (duplicateName ? terGroups.DUPLICATE_NAME_MESSAGE : undefined);
  const workflowValid = terGroups.workflowValid(metadata.workflow);
  const workflowError =
    workflowServerError ||
    (workflowTouched && !workflowValid ? terGroups.WORKFLOW_REQUIRED_MESSAGE : undefined);

  const create = (ters: terGroups.Ter[]): Promise<void> => {
    setSubmitting(true);
    setNameError(undefined);
    setWorkflowServerError(undefined);
    const request = terGroups.toCreateRequest(groupName, metadata, ters);
    return apiClient.httpPost(terGroups.TER_GROUPS_PATH, request).then(
      response => {
        navigate(terGroups.terGroupRoute((response as terGroups.TerGroup).terGroup));
      },
      (error: ApiError) => {
        setSubmitting(false);
        setNameError(terGroups.fieldErrorOf(error, 'groupName'));
        setWorkflowServerError(terGroups.fieldErrorOf(error, 'workflow'));
        throw error;
      }
    );
  };

  return (
    <SpaceBetween size="m">
      {localeErrors.length > 0 && (
        <RetryAlert
          id="terGroupLocalesError"
          header="Could not load the locales; creating is unavailable"
          messages={localeErrors}
          retryId="retryLocales"
          onRetry={loadLocales}
        />
      )}
      <ArcRowsUpload
        list={terGroups.terRows}
        locales={locales}
        display={formatLocale}
        compareRows={byArc}
        idPrefix="terGroup"
        title="Create TER group"
        description="Name the group and set its default TERs, then fill in the template with one row per arc and upload it."
        templateUrl={TEMPLATE_URL}
        emptyMessage="The file has no TERs below the header row."
        ready={
          groupName.trim() !== '' &&
          !duplicateName &&
          workflowValid &&
          terGroups.defaultTersValid(metadata)
        }
        onCreate={create}
        describeSaveError={terGroups.describeCreateError}
        conflictHeader={terGroups.CONFLICT_HEADER}
        onReload={loadLocales}
      >
        <FormField
          id="groupName-field"
          label="Group name"
          constraintText="Required. The name cannot be changed later."
          errorText={groupNameError}
        >
          <Input
            id="groupName"
            value={groupName}
            invalid={Boolean(groupNameError)}
            disabled={submitting}
            onChange={({ detail }): void => {
              setGroupName(detail.value);
              setNameError(undefined);
            }}
          />
        </FormField>
        {terGroups.DEFAULT_TERS.map(({ field, label }) => (
          <DefaultTerField
            key={field}
            field={field}
            label={label}
            values={metadata}
            disabled={submitting}
            onChange={setMetadata}
          />
        ))}
        <FormField
          id="workflow-field"
          label={WORKFLOW.label}
          constraintText="Required. The workflow cannot be changed later."
          errorText={workflowError}
        >
          <MetadataInput
            field="workflow"
            label={WORKFLOW.label}
            values={metadata}
            disabled={submitting}
            invalid={Boolean(workflowError)}
            onBlur={(): void => setWorkflowTouched(true)}
            onChange={(values): void => {
              setMetadata(values);
              setWorkflowTouched(true);
              setWorkflowServerError(undefined);
            }}
          />
        </FormField>
        {OPTIONAL_FIELDS.map(({ field, label }) => (
          <FormField
            key={field}
            id={`${field}-field`}
            label={label}
            description="Optional"
            constraintText="Cannot be changed later."
          >
            <MetadataInput
              field={field}
              label={label}
              values={metadata}
              disabled={submitting}
              onChange={setMetadata}
            />
          </FormField>
        ))}
      </ArcRowsUpload>
    </SpaceBetween>
  );
};

TerGroupCreate.propTypes = {
  apiClient: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  canWrite: PropTypes.bool.isRequired,
};

const TerGroupCreateView = (props: Omit<TerGroupCreateProps, 'apiClient'>): JSX.Element | null => (
  <TerGroupCreate apiClient={ApiClient} {...props} />
);

export default TerGroupCreateView;
